import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { useSplitReveal } from '../lib/useSplitReveal';
import { useScrollReveal } from '../lib/useScrollReveal';

/**
 * Three real screens, each with its own plate. The screenshots are 696x1512,
 * so the frames carry that exact ratio: nothing is cropped, scaled or drifted.
 */
const SCREENS = [
  {
    src: '/app_1.png',
    title: 'Your feed',
    body: 'Categories your partner stores carry, filtered down to your colours.',
    alt: 'The DressUp home screen showing a personal colour palette above matching items',
  },
  {
    src: '/app_2.png',
    title: 'The stores',
    body: 'Khaadi, LAMA and the rest. Checkout stays on the store’s own site.',
    alt: 'The partner store list inside the DressUp app',
  },
  {
    src: '/app_3.png',
    title: 'Your shades',
    body: 'Thirty colours, sorted by mood, with the reasoning written out.',
    alt: 'A warm-undertone palette breakdown inside the DressUp app',
  },
] as const;

/**
 * A handset around each screenshot. The page is otherwise square-cornered on
 * purpose, and this is the one exception that earns itself: the radius, the
 * bezel and the island are what make a flat PNG read as a phone rather than as
 * a floating rectangle. The screen keeps the screenshots' own 696x1512 ratio.
 */
const PhoneFrame: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '',
  children,
}) => (
  <div
    className={`relative rounded-[1.5rem] bg-[#16161a] p-[5px] shadow-panel ring-1 ring-white/10 lg:rounded-[2rem] lg:p-[7px] ${className}`}
  >
    {/* Side hardware, drawn as slivers rather than borders so they keep their
        width at every frame size. */}
    <span aria-hidden="true" className="absolute -left-px top-[17%] h-[4%] w-px bg-white/20" />
    <span aria-hidden="true" className="absolute -left-px top-[24%] h-[7%] w-px bg-white/20" />
    <span aria-hidden="true" className="absolute -left-px top-[33%] h-[7%] w-px bg-white/20" />
    <span aria-hidden="true" className="absolute -right-px top-[26%] h-[10%] w-px bg-white/20" />

    <div className="relative aspect-[696/1512] w-full overflow-hidden rounded-[1.15rem] bg-surface lg:rounded-[1.6rem]">
      {children}
      {/* The screenshots carry no status bar, so the island sits in the gap
          above the app's own header rather than over anything. */}
      <span
        aria-hidden="true"
        className="absolute left-1/2 top-[0.9%] h-[1.4%] w-[22%] -translate-x-1/2 rounded-full bg-black/90"
      />
    </div>
  </div>
);

const AppShowcaseSection: React.FC = () => {
  const heading = useSplitReveal<HTMLHeadingElement>({ onScroll: true });
  // The frames stagger in, but nothing inside them moves or scales: these are
  // UI screenshots, and a parallax drift would crop the thing being shown.
  const screens = useScrollReveal<HTMLDivElement>({ selector: 'figure', y: 24, stagger: 0.1 });

  // Phones step through the screens one at a time, the same way the Shopify
  // section steps through its three steps.
  const [active, setActive] = useState(0);
  const screen = SCREENS[active];

  return (
    <section className="screen items-stretch">
      <div className="shell flex flex-col justify-center py-8 lg:py-16">
        {/*
          Asymmetric split from lg up: the argument holds one narrow column on
          the left, the three screens take the wider half and sit on a broken
          baseline. Below lg the header stacks over the screens.
        */}
        <div className="rule pt-6 lg:grid lg:grid-cols-12 lg:items-center lg:gap-x-10 lg:pt-12">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
              Inside the app
            </p>

            <h2
              ref={heading}
              className="display split-target mt-4 max-w-[16ch] text-[clamp(1.75rem,7vw,3.25rem)] font-medium text-ink sm:mt-5"
            >
              The whole storefront, rebuilt for one person.
            </h2>

            <p className="mt-4 max-w-[46ch] text-[14px] leading-relaxed text-muted sm:mt-6 sm:text-[15px]">
              Your colors, your size, your brands. Nothing else in the feed.
            </p>

            <a
              href="https://app.thedressup.co/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost group mt-6 hidden sm:inline-flex lg:mt-8"
            >
              Open the web app
              <ArrowUpRight
                strokeWidth={1.5}
                className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </div>

          {/* From sm up all three read at once: a snap row on a tablet, the
              staggered field on a desktop. */}
          <div
            ref={screens}
            className="-mx-5 mt-8 hidden snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-1 md:-mx-10 md:px-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex lg:col-span-8 lg:mx-0 lg:mt-0 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:px-0 lg:pb-0"
          >
            {SCREENS.map((item, i) => (
              <figure
                key={item.src}
                className="group w-[28vw] shrink-0 snap-start lg:w-full"
              >
                <PhoneFrame className="transition-transform duration-300 ease-out group-hover:-translate-y-1">
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-contain"
                  />
                </PhoneFrame>

                {/* Museum plate: index, name, one line on what the screen does. */}
                <figcaption
                  className={`mt-4 flex items-baseline gap-3 border-t pt-3 ${
                    i === 0 ? 'border-accent' : 'border-line'
                  }`}
                >
                  <span className="shrink-0 text-[12px] font-semibold tabular-nums text-accent">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-ink">{item.title}</span>
                    <span className="mt-1 block max-w-[30ch] text-[13px] leading-relaxed text-muted">
                      {item.body}
                    </span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>

          {/*
            Phone: one screen at a time. A row of three would shrink each one
            past reading, and a scroll row leaves the section taller than the
            single screen the page is built on. The screenshot keeps its own
            ratio beside its plate, and the stepper below drives the set.
          */}
          <div className="mt-6 sm:hidden">
            <figure aria-live="polite" className="flex gap-4 border-t border-accent pt-4">
              {/*
                All three stay mounted and cross-fade. Swapping one img's src
                leaves the frame blank for a beat while the next screenshot
                decodes, which reads as a broken step.
              */}
              <PhoneFrame className="w-[44vw] shrink-0">
                {SCREENS.map((item, i) => (
                  <img
                    key={item.src}
                    src={item.src}
                    alt={item.alt}
                    aria-hidden={i !== active}
                    loading="lazy"
                    decoding="async"
                    className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 ease-out ${
                      i === active ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </PhoneFrame>

              <figcaption className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold tabular-nums text-muted">
                  Screen {active + 1} of {SCREENS.length}
                </p>
                <h3 className="mt-2 text-[17px] font-semibold text-ink">{screen.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-muted">{screen.body}</p>

                <a
                  href="https://app.thedressup.co/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-ink underline decoration-line underline-offset-4 transition-colors hover:decoration-ink"
                >
                  Open the web app
                  <ArrowUpRight strokeWidth={1.5} className="h-4 w-4" />
                </a>
              </figcaption>
            </figure>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center">
                {SCREENS.map((item, i) => (
                  <button
                    key={item.src}
                    type="button"
                    onClick={() => setActive(i)}
                    aria-label={`Show screen ${i + 1}: ${item.title}`}
                    aria-current={i === active}
                    className="flex h-11 w-8 items-center justify-center"
                  >
                    <span
                      className={`h-1.5 w-1.5 transition-colors ${
                        i === active ? 'bg-accent' : 'bg-line'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActive((i) => Math.max(0, i - 1))}
                  disabled={active === 0}
                  aria-label="Previous screen"
                  className="flex h-11 w-11 items-center justify-center border border-line text-ink transition-colors hover:border-ink disabled:opacity-35"
                >
                  <ArrowLeft strokeWidth={1.5} className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setActive((i) => Math.min(SCREENS.length - 1, i + 1))}
                  disabled={active === SCREENS.length - 1}
                  aria-label="Next screen"
                  className="flex h-11 w-11 items-center justify-center border border-line text-ink transition-colors hover:border-ink disabled:opacity-35"
                >
                  <ArrowRight strokeWidth={1.5} className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcaseSection;
